import type { Maybe, SteamWorkshopItem } from "@/graphql";
import { SteamWorkshopItemType } from "@/graphql";
import type { ComputedRef, WritableComputedRef } from "vue";
import { computed } from "vue";
import { i18n } from "@/i18n";

/**
 * Base Steam workshop interface. Each game has to implement the used settings (see CS:GO game profile).
 * @param selectedWorkshopItems The full current selected workshop items, as resolved by the backend
 * @param subscribedFileIds The current subscribed file IDs
 * @param subscribedCollectionIds The current collection IDs
 * @param modExtension Optional, the mod file extension to be appended to the modID whilst adding a mod
 * @param allowModsReordering Optional, flag used by ModDrag component to determine if the mod reordering buttons should be shown
 * @param isCurseForgeMod Optional, flag used to specify if the mods are from curseForge so that the link button with the appropriate text is rendered
 * @param modPrefix Optional, a repo-key prefix stored values carry (e.g. Palworld stores source-prefixed
 *   repo keys like `mod.steam.workshop.{id}` in a list shared with other mod sources). When set, only
 *   entries starting with the prefix belong to this tab — foreign-source entries are preserved untouched
 *   by every operation, and added mods are stored as `${modPrefix}${id}`.
 */
export function useSteamWorkshop(
  selectedWorkshopItems: ComputedRef<Maybe<SteamWorkshopItem>[]>,
  subscribedFileIds: WritableComputedRef<string[]>,
  subscribedCollectionIds: WritableComputedRef<string[]>,
  modExtension: string = "",
  allowModsReordering: boolean = false,
  isCurseForgeMod: boolean = false,
  modPrefix: string = "",
) {
  // 🚨 The global instance, not `useI18n()`. `ModTags` calls `useMods()` — and therefore this — from
  // inside a computed during *render*, where vue-i18n has no setup context and throws "Must be
  // called at the top of a `setup` function". That throw happens inside a scheduler flush and takes
  // the whole flush down with it, including the save bar's debounced change watcher: adding a mod
  // showed the mod in the list and no Save button, intermittently, depending on which effects
  // happened to share the flush.
  const { t } = i18n.global;
  const storeTitle = computed(() =>
    isCurseForgeMod ? t("ngp/mods/dialog/link/curseforge") : t("ngp/mods/dialog/link/steamworkshop"),
  );

  const installedModIds = computed(() =>
    (subscribedFileIds.value ?? [])
      .filter((mod) => !modPrefix || mod.startsWith(modPrefix))
      .map((mod) => extractModID(mod, modExtension))
      .concat(subscribedCollectionIds.value ?? []),
  );

  // With a prefix, resolved items may span multiple sources whose numeric IDs can collide —
  // only match items whose repoKey belongs to this tab's source.
  const ownSelectedWorkshopItems = computed(() =>
    modPrefix
      ? selectedWorkshopItems.value.filter((mod) => mod?.repoKey?.startsWith(modPrefix))
      : selectedWorkshopItems.value,
  );
  const installedMods = computed(
    (): Array<SteamWorkshopItem> =>
      installedModIds.value
        .map((modId: string) =>
          ownSelectedWorkshopItems.value.find((mod: Maybe<SteamWorkshopItem>) => mod?.id.toString() == modId),
        )
        .filter((mod): mod is SteamWorkshopItem => mod != null),
  );

  function addMod(mod: SteamWorkshopItem) {
    if (mod.itemType == SteamWorkshopItemType.File && findModIndexFromSubscribedFileIds(mod) === -1) {
      if (modExtension.length > 0) {
        subscribedFileIds.value.push(`*${mod.id.toString()}${modExtension}`);
      } else {
        subscribedFileIds.value.push(`${modPrefix}${mod.id.toString()}`);
      }
      if (!allowModsReordering) {
        subscribedFileIds.value.sort();
      }
    } else if (
      mod.itemType == SteamWorkshopItemType.Collection &&
      !subscribedCollectionIds.value.includes(mod.id.toString())
    ) {
      subscribedCollectionIds.value.push(mod.id.toString());
      subscribedCollectionIds.value.sort();
    } else {
      return;
    }
    selectedWorkshopItems.value.push(mod);
  }

  function removeMod(mod: SteamWorkshopItem) {
    if (mod.itemType == SteamWorkshopItemType.File) {
      const index = findModIndexFromSubscribedFileIds(mod);
      if (index > -1) {
        subscribedFileIds.value.splice(index, 1);
      }
    } else if (mod.itemType == SteamWorkshopItemType.Collection) {
      const index = subscribedCollectionIds.value.indexOf(mod.id.toString());
      if (index > -1) {
        subscribedCollectionIds.value.splice(index, 1);
      }
    }
  }

  function modTags(mod: SteamWorkshopItem) {
    return [mod.itemType.toString()];
  }

  function isModActivated(mod: SteamWorkshopItem) {
    const index = findModIndexFromSubscribedFileIds(mod);
    return index > -1 && subscribedFileIds.value[index].startsWith("*");
  }

  function toggleModActivation(mod: SteamWorkshopItem) {
    const index = findModIndexFromSubscribedFileIds(mod);
    if (index > -1) {
      if (subscribedFileIds.value[index].startsWith("*")) {
        subscribedFileIds.value[index] = subscribedFileIds.value[index].slice(1);
      } else {
        subscribedFileIds.value[index] = "*" + subscribedFileIds.value[index];
      }
    }
  }

  function moveModUp(mod: SteamWorkshopItem) {
    const index = findModIndexFromSubscribedFileIds(mod);
    if (index - 1 >= 0) {
      const temp = subscribedFileIds.value[index];
      subscribedFileIds.value[index] = subscribedFileIds.value[index - 1];
      subscribedFileIds.value[index - 1] = temp;
    }
  }

  function moveModDown(mod: SteamWorkshopItem) {
    const index = findModIndexFromSubscribedFileIds(mod);
    if (index + 1 < subscribedFileIds.value.length) {
      const temp = subscribedFileIds.value[index];
      subscribedFileIds.value[index] = subscribedFileIds.value[index + 1];
      subscribedFileIds.value[index + 1] = temp;
    }
  }

  function extractModID(modValue: string, extension: string) {
    const value = modPrefix && modValue.startsWith(modPrefix) ? modValue.slice(modPrefix.length) : modValue;
    return extension.length ? value.replace("*", "").replace(extension, "") : value;
  }

  function findModIndexFromSubscribedFileIds(mod: SteamWorkshopItem) {
    if (modExtension.length > 0) {
      return subscribedFileIds.value.findIndex((sub) => sub.includes(mod.id.toString()));
    } else {
      return subscribedFileIds.value.findIndex((sub) => sub === `${modPrefix}${mod.id.toString()}`);
    }
  }

  return {
    storeTitle,
    installedMods,
    addMod,
    removeMod,
    modTags,
    moveModUp,
    moveModDown,
    isModActivated,
    toggleModActivation,
    subscribedFileIds,
  };
}
